/**
 * What each confirm dialog of the Abby welcome tab says (spec 2026-10-23 §4.2,
 * §5). Pure, so the words are tested
 * (tests/unit/admin/app-experience-welcome-state.test.ts) and the tab only
 * wires them. Same shape and same delay sentence as the intro tab
 * (./confirm-requests.ts).
 *
 * Turning the welcome OFF is different from the intro: the server marks the
 * welcome as seen for every member who opens the app while it is off, on every
 * app version (spec §4.2). So the moment passes for those members, and the
 * dialog says so before the admin confirms.
 */
import { DELAY, type ConfirmRequest } from './confirm-requests';
import type { ExperienceLocale } from './contract';
import { LANGUAGE_NAME } from './intro-state';
import { CUSTOM_TEXT_REACH, type WelcomeMode, type WelcomeRestorePlan, type WelcomeSwitchState } from './welcome-state';

const NOTE_LABEL = 'Note (optional)';
const OLDER_APPS = 'Older app versions show the built-in text, not the video.';

export function welcomeSwitchRequest(sw: Pick<WelcomeSwitchState, 'on'>, dirty: boolean): ConfirmRequest {
  if (sw.on) {
    return {
      title: 'Turn the Abby welcome off?',
      lines: [
        'New members do not see the Abby welcome. This works at once, on every app version.',
        'A member who joins while the welcome is off never sees it, even if you turn it on again later.',
        DELAY,
      ],
      confirmLabel: 'Turn off',
      noteLabel: NOTE_LABEL,
      destructive: true,
      success: `The Abby welcome is off. ${DELAY}`,
    };
  }
  return {
    title: 'Turn the Abby welcome on?',
    lines: [
      'New members see the Abby welcome the first time they open the Abby tab.',
      ...(dirty ? ['Your changes that are not published stay as a draft. This turns on the newest published version only.'] : []),
      DELAY,
    ],
    confirmLabel: 'Turn on',
    noteLabel: NOTE_LABEL,
    success: `The Abby welcome is on. ${DELAY}`,
  };
}

export function welcomePublishRequest(args: {
  mode: WelcomeMode;
  liveOn: boolean;
  warnings: string[];
  textNotSent: boolean;
  /** The version has your own text and voice (voice mode, `voice` is not null). */
  customVoice: boolean;
}): ConfirmRequest {
  const { mode, liveOn, warnings, textNotSent, customVoice } = args;
  // Which app versions show it: the video and your own text need app 1.0.5 (spec §6.1).
  const reach = mode === 'video' ? [OLDER_APPS] : customVoice ? [CUSTOM_TEXT_REACH] : [];
  return {
    title: 'Publish this version?',
    lines: liveOn
      ? ['New members get this version.', ...reach, DELAY]
      : ['The Abby welcome is off. This saves the version. Members do not see it until you turn the welcome on.', ...reach],
    warnings: [
      ...warnings,
      ...(textNotSent ? ['Your changes to the text are not published. In video mode, members see the video.'] : []),
    ],
    confirmLabel: 'Publish',
    noteLabel: NOTE_LABEL,
    success: liveOn ? `The new version is published. ${DELAY}` : 'The version is saved. The Abby welcome is still off.',
  };
}

export function welcomeRestoreRequest(version: number, plan: WelcomeRestorePlan, liveOn: boolean): ConfirmRequest {
  // Members notice a restore only when the welcome is on before or after it.
  const reaches = plan.config.enabled || liveOn;
  return {
    title: `Restore version ${version}?`,
    lines: [`This publishes version ${version} again as the newest version.`, ...plan.effects, ...(reaches ? [DELAY] : [])],
    confirmLabel: `Restore version ${version}`,
    noteLabel: NOTE_LABEL,
    success: `Version ${version} is restored.`,
  };
}

export function welcomeDiscardRequest(): ConfirmRequest {
  return {
    title: 'Discard your changes?',
    lines: [
      'The page goes back to the newest published version.',
      "Abby's voice that you generated and did not publish is removed from this page.",
      'A video that you uploaded and did not publish is removed from this page.',
    ],
    confirmLabel: 'Discard changes',
    noteLabel: null,
    destructive: true,
    success: 'Your changes are discarded.',
  };
}

/** "Reset to the built-in text" for one language. Publishes nothing, so no note. */
export function welcomeResetRequest(locale: ExperienceLocale): ConfirmRequest {
  const name = LANGUAGE_NAME[locale];
  return {
    title: `Reset the ${name} text?`,
    lines: [`The ${name} text goes back to the built-in text.`, `Your changes to the ${name} text are lost. Nothing is published until you publish.`],
    confirmLabel: 'Reset the text',
    noteLabel: null,
    destructive: true,
    success: `The ${name} text is the built-in text again.`,
  };
}
